import * as React from "react";
import { useMutation } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useUserFinancialSettings } from "@/hooks/useAccounting";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { formatCurrency } from "@/lib/format";

type OrderingHoldOverride = {
  enabled: boolean;
  reason: string;
  setBy: string;
  setAt: number;
  expiresAt?: number;
};

function describeOrderingHold(override: OrderingHoldOverride | null): {
  value: string;
  hint?: string;
} {
  if (!override?.enabled) {
    return { value: "Off" };
  }
  if (override.expiresAt && override.expiresAt <= Date.now()) {
    return {
      value: `Expired ${new Date(override.expiresAt).toLocaleDateString()}`,
      hint: override.reason,
    };
  }
  return {
    value: override.expiresAt
      ? `Active until ${new Date(override.expiresAt).toLocaleDateString()}`
      : "Active (no expiry)",
    hint: override.reason,
  };
}

export function FinancialSettingsTab({ userId }: { userId: string }) {
  const settings = useUserFinancialSettings(userId);

  const [editDialog, setEditDialog] = React.useState<
    "credit" | "tier" | "enterprise" | "terms" | "orderingHold" | null
  >(null);

  if (!settings) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Loading financial settings...
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <SettingCard
          label="Credit Limit"
          value={formatCurrency(settings.creditLimit ?? 0)}
          hint={`Used: ${formatCurrency(settings.creditUsed ?? 0)} · Available: ${formatCurrency(settings.creditAvailable ?? 0)}`}
          onEdit={() => setEditDialog("credit")}
        />
        <SettingCard
          label="Pricing Tier"
          value={settings.tier ? `Tier ${settings.tier}` : "Not set"}
          onEdit={() => setEditDialog("tier")}
        />
        <SettingCard
          label="Account Type"
          value={settings.isEnterprise ? "Enterprise" : "Standard"}
          onEdit={() => setEditDialog("enterprise")}
        />
        <SettingCard
          label="Payment Terms"
          value={
            settings.paymentTerms
              ? `Net ${settings.paymentTerms}`
              : "Not set"
          }
          onEdit={() => setEditDialog("terms")}
        />
        <SettingCard
          label="Ordering Hold Override"
          {...describeOrderingHold(settings.orderingHoldOverride ?? null)}
          onEdit={() => setEditDialog("orderingHold")}
        />
      </div>

      {editDialog === "credit" ? (
        <EditCreditDialog
          userId={userId}
          current={settings.creditLimit ?? 0}
          onClose={() => setEditDialog(null)}
        />
      ) : null}
      {editDialog === "tier" ? (
        <EditTierDialog
          userId={userId}
          current={settings.tier ?? null}
          onClose={() => setEditDialog(null)}
        />
      ) : null}
      {editDialog === "enterprise" ? (
        <EditEnterpriseDialog
          userId={userId}
          current={settings.isEnterprise ?? false}
          onClose={() => setEditDialog(null)}
        />
      ) : null}
      {editDialog === "terms" ? (
        <EditPaymentTermsDialog
          userId={userId}
          current={settings.paymentTerms ?? null}
          onClose={() => setEditDialog(null)}
        />
      ) : null}
      {editDialog === "orderingHold" ? (
        <EditOrderingHoldDialog
          userId={userId}
          current={settings.orderingHoldOverride ?? null}
          onClose={() => setEditDialog(null)}
        />
      ) : null}
    </div>
  );
}

function SettingCard({
  label,
  value,
  hint,
  onEdit,
}: {
  label: string;
  value: string;
  hint?: string;
  onEdit: () => void;
}) {
  return (
    <Card>
      <CardContent className="flex items-center justify-between p-4">
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-lg font-semibold">{value}</p>
          {hint ? (
            <p className="text-xs text-muted-foreground">{hint}</p>
          ) : null}
        </div>
        <Button variant="outline" size="sm" onClick={onEdit}>
          Edit
        </Button>
      </CardContent>
    </Card>
  );
}

function EditCreditDialog({
  userId,
  current,
  onClose,
}: {
  userId: string;
  current: number;
  onClose: () => void;
}) {
  const mutation = useMutation(api.accounting.updateCreditLimit);
  const [newLimit, setNewLimit] = React.useState(String(current));
  const [reason, setReason] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);

  const parsed = parseFloat(newLimit) || 0;
  const isLargeDecrease = current > 0 && parsed < current * 0.8;

  const handleSave = React.useCallback(async () => {
    if (!reason.trim()) {
      toast.error("Reason is required");
      return;
    }
    setIsSaving(true);
    try {
      await mutation({
        userId: userId as any,
        creditLimit: parsed,
        reason,
      });
      toast.success("Credit limit updated");
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update credit limit",
      );
    } finally {
      setIsSaving(false);
    }
  }, [userId, parsed, reason, mutation, onClose]);

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Credit Limit</DialogTitle>
          <DialogDescription>
            Current limit: {formatCurrency(current)}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>New Credit Limit ($)</Label>
            <Input
              type="number"
              step="0.01"
              min={0}
              value={newLimit}
              onChange={(e) => setNewLimit(e.target.value)}
            />
          </div>
          {isLargeDecrease ? (
            <p className="text-sm text-destructive">
              This is a 20%+ decrease from the current limit. Please ensure this
              is intentional.
            </p>
          ) : null}
          <div className="grid gap-2">
            <Label>Reason (required)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this change being made?"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={isSaving || !reason.trim()}
          >
            {isSaving ? "Saving..." : "Update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditTierDialog({
  userId,
  current,
  onClose,
}: {
  userId: string;
  current: number | null;
  onClose: () => void;
}) {
  const mutation = useMutation(api.accounting.updateUserTier);
  const [tier, setTier] = React.useState(String(current ?? ""));
  const [reason, setReason] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);

  const handleSave = React.useCallback(async () => {
    if (!tier || !reason.trim()) {
      toast.error("Tier and reason are required");
      return;
    }
    setIsSaving(true);
    try {
      await mutation({
        userId: userId as any,
        tier: parseInt(tier),
        reason,
      });
      toast.success("Tier updated");
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update tier",
      );
    } finally {
      setIsSaving(false);
    }
  }, [userId, tier, reason, mutation, onClose]);

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Pricing Tier</DialogTitle>
          <DialogDescription>
            Current tier: {current ? `Tier ${current}` : "Not set"}. Tier 1 has
            the best pricing; higher tier numbers have higher prices.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Tier</Label>
            <Select value={tier} onValueChange={setTier}>
              <SelectTrigger>
                <SelectValue placeholder="Select tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Tier 1 - Diamond</SelectItem>
                <SelectItem value="2">Tier 2 - Platinum</SelectItem>
                <SelectItem value="3">Tier 3 - Gold</SelectItem>
                <SelectItem value="4">Tier 4 - Silver</SelectItem>
                <SelectItem value="5">Tier 5 - Standard</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Reason (required)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this change being made?"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={isSaving || !tier || !reason.trim()}
          >
            {isSaving ? "Saving..." : "Update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditEnterpriseDialog({
  userId,
  current,
  onClose,
}: {
  userId: string;
  current: boolean;
  onClose: () => void;
}) {
  const mutation = useMutation(api.accounting.updateEnterpriseStatus);
  const [isEnterprise, setIsEnterprise] = React.useState(current);
  const [reason, setReason] = React.useState("");
  const [confirmed, setConfirmed] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  const changed = isEnterprise !== current;

  const handleSave = React.useCallback(async () => {
    if (!reason.trim() || !confirmed) {
      toast.error("Reason and confirmation are required");
      return;
    }
    setIsSaving(true);
    try {
      await mutation({
        userId: userId as any,
        isEnterprise,
        reason,
      });
      toast.success("Enterprise status updated");
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to update enterprise status",
      );
    } finally {
      setIsSaving(false);
    }
  }, [userId, isEnterprise, reason, confirmed, mutation, onClose]);

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Enterprise Status</DialogTitle>
          <DialogDescription>
            Current: {current ? "Enterprise" : "Standard"}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="enterprise"
              checked={isEnterprise}
              onChange={(e) => setIsEnterprise(e.target.checked)}
              className="h-4 w-4"
            />
            <Label htmlFor="enterprise">Enterprise Account</Label>
          </div>
          <div className="grid gap-2">
            <Label>Reason (required)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this change being made?"
            />
          </div>
          {changed ? (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="confirm"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="h-4 w-4"
              />
              <Label htmlFor="confirm" className="text-sm">
                I confirm this change to{" "}
                {isEnterprise ? "Enterprise" : "Standard"}
              </Label>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={isSaving || !changed || !reason.trim() || !confirmed}
          >
            {isSaving ? "Saving..." : "Update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditPaymentTermsDialog({
  userId,
  current,
  onClose,
}: {
  userId: string;
  current: number | null;
  onClose: () => void;
}) {
  const mutation = useMutation(api.accounting.updatePaymentTerms);
  const [terms, setTerms] = React.useState(
    current ? String(current) : "none",
  );
  const [reason, setReason] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);

  const handleSave = React.useCallback(async () => {
    if (!reason.trim()) {
      toast.error("Reason is required");
      return;
    }
    setIsSaving(true);
    try {
      await mutation({
        userId: userId as any,
        paymentTerms: terms === "none" ? undefined : parseInt(terms),
        reason,
      });
      toast.success("Payment terms updated");
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to update payment terms",
      );
    } finally {
      setIsSaving(false);
    }
  }, [userId, terms, reason, mutation, onClose]);

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Payment Terms</DialogTitle>
          <DialogDescription>
            Current: {current ? `Net ${current}` : "Not set"}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Payment Terms</Label>
            <Select value={terms} onValueChange={setTerms}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="10">Net 10</SelectItem>
                <SelectItem value="15">Net 15</SelectItem>
                <SelectItem value="30">Net 30</SelectItem>
                <SelectItem value="60">Net 60</SelectItem>
                <SelectItem value="90">Net 90</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Reason (required)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this change being made?"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={isSaving || !reason.trim()}
          >
            {isSaving ? "Saving..." : "Update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ORDERING_HOLD_EXPIRY_OPTIONS = [
  { value: "7", label: "7 days" },
  { value: "14", label: "14 days" },
  { value: "30", label: "30 days" },
] as const;

function EditOrderingHoldDialog({
  userId,
  current,
  onClose,
}: {
  userId: string;
  current: OrderingHoldOverride | null;
  onClose: () => void;
}) {
  const { authUserId } = useAuthIdentity();
  const mutation = useMutation(api.admin.setOrderingHoldOverride);
  const isActive =
    current?.enabled === true &&
    (!current.expiresAt || current.expiresAt > Date.now());
  const [reason, setReason] = React.useState("");
  const [expiryDays, setExpiryDays] = React.useState("14");
  const [isSaving, setIsSaving] = React.useState(false);

  const handleSave = React.useCallback(
    async (enabled: boolean) => {
      if (!authUserId) {
        toast.error("Not authenticated");
        return;
      }
      if (!reason.trim()) {
        toast.error("Reason is required");
        return;
      }
      setIsSaving(true);
      try {
        await mutation({
          authUserId,
          userId: userId as any,
          enabled,
          reason,
          expiresAt: enabled
            ? Date.now() + parseInt(expiryDays) * 24 * 60 * 60 * 1000
            : undefined,
        });
        toast.success(
          enabled
            ? "Ordering hold override enabled"
            : "Ordering hold override removed",
        );
        onClose();
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to update override",
        );
      } finally {
        setIsSaving(false);
      }
    },
    [authUserId, userId, reason, expiryDays, mutation, onClose],
  );

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ordering Hold Override</DialogTitle>
          <DialogDescription>
            Lets this customer keep placing orders while a failed or retrying
            payment is outstanding. Credit-limit and overdue blocks still
            apply, and collections continue unchanged.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {current?.enabled ? (
            <div className="rounded-md border p-3 text-sm">
              <p>
                <span className="text-muted-foreground">Current: </span>
                {isActive ? "Active" : "Expired"}
                {current.expiresAt
                  ? ` · until ${new Date(current.expiresAt).toLocaleDateString()}`
                  : ""}
              </p>
              <p className="text-muted-foreground">{current.reason}</p>
            </div>
          ) : null}
          {!isActive ? (
            <div className="grid gap-2">
              <Label>Expires after</Label>
              <Select value={expiryDays} onValueChange={setExpiryDays}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORDERING_HOLD_EXPIRY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <div className="grid gap-2">
            <Label>Reason (required)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                isActive
                  ? "Why is the override being removed?"
                  : "Why is this customer allowed to order despite a failed payment?"
              }
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {isActive ? (
            <Button
              variant="destructive"
              onClick={() => void handleSave(false)}
              disabled={isSaving || !reason.trim()}
            >
              {isSaving ? "Saving..." : "Remove Override"}
            </Button>
          ) : (
            <Button
              onClick={() => void handleSave(true)}
              disabled={isSaving || !reason.trim()}
            >
              {isSaving ? "Saving..." : "Enable Override"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
